import React, { useState, useEffect, useRef, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001';

const getToken = () => localStorage.getItem('policylink_token');

const authFetch = async (url, options = {}) => {
  const token = getToken();
  const headers = {
    ...options.headers,
    'Authorization': `Bearer ${token}`,
  };
  const response = await fetch(url, { ...options, headers });
  if (response.status === 401) {
    localStorage.removeItem('policylink_token');
    localStorage.removeItem('policylink_user');
    window.location.reload();
    throw new Error('Session expired');
  }
  return response;
};

// ============================================
// SUPER ADMIN PANEL
// ============================================

function SuperAdmin() {
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [tenantSources, setTenantSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('users');

  // Prevent modal close on drag-release outside modal
  const mouseDownTarget = useRef(null);
  const handleOverlayMouseDown = useCallback((e) => { mouseDownTarget.current = e.target; }, []);
  const handleOverlayClick = useCallback((e, closeFn) => {
    if (e.target === e.currentTarget && mouseDownTarget.current === e.currentTarget) {
      closeFn();
    }
    mouseDownTarget.current = null;
  }, []);

  // Modal states
  const [showCreateTenant, setShowCreateTenant] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(null);
  const [showEditTenant, setShowEditTenant] = useState(null);
  const [showCreateSource, setShowCreateSource] = useState(false);
  const [showEditSource, setShowEditSource] = useState(null);

  // Form states
  const [newTenant, setNewTenant] = useState({ name: '', slug: '' });
  const [newUser, setNewUser] = useState({ email: '', name: '', role: 'user', can_view_claims: true });
  const [createSources, setCreateSources] = useState([]);
  const [editUserData, setEditUserData] = useState({});
  const [editTenantData, setEditTenantData] = useState({});
  const [newSource, setNewSource] = useState({ name: '', endpoint: '', company_id: 1, username: '', password: '' });
  const [editSourceData, setEditSourceData] = useState({});
  const [saving, setSaving] = useState(false);

  // Modal-level error (shown inside modals)
  const [modalError, setModalError] = useState(null);

  // Test connection state
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetchTenants();
  }, []);

  useEffect(() => {
    if (selectedTenant) {
      setError(null);
      fetchTenantUsers(selectedTenant.id);
      fetchTenantSources(selectedTenant.id);
    }
  }, [selectedTenant]);

  const fetchTenants = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${API_BASE}/api/admin/tenants`);
      if (!res.ok) throw new Error('Failed to fetch tenants');
      const data = await res.json();
      setTenants(data);
      if (data.length > 0 && !selectedTenant) {
        setSelectedTenant(data[0]);
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const fetchTenantUsers = async (tenantId) => {
    try {
      const res = await authFetch(`${API_BASE}/api/admin/tenants/${tenantId}/users`);
      if (!res.ok) throw new Error('Failed to fetch users');
      const data = await res.json();
      setTenantUsers(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchTenantSources = async (tenantId) => {
    try {
      const res = await authFetch(`${API_BASE}/api/admin/tenants/${tenantId}/sources`);
      if (!res.ok) throw new Error('Failed to fetch sources');
      const data = await res.json();
      setTenantSources(data);
    } catch (err) {
      setError(err.message);
    }
  };

  // ---- Tenant handlers ----

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    setSaving(true);
    setModalError(null);
    try {
      const res = await authFetch(`${API_BASE}/api/admin/tenants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newTenant),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to create tenant');
      }
      setShowCreateTenant(false);
      setNewTenant({ name: '', slug: '' });
      await fetchTenants();
    } catch (err) {
      setModalError(err.message);
    }
    setSaving(false);
  };

  const handleUpdateTenant = async (e) => {
    e.preventDefault();
    if (!showEditTenant) return;
    setSaving(true);
    setModalError(null);
    try {
      const res = await authFetch(`${API_BASE}/api/admin/tenants/${showEditTenant.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editTenantData),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to update tenant');
      }
      setShowEditTenant(null);
      setEditTenantData({});
      await fetchTenants();
    } catch (err) {
      setModalError(err.message);
    }
    setSaving(false);
  };

  const openEditTenant = (tenant) => {
    setShowEditTenant(tenant);
    setEditTenantData({
      name: tenant.name,
      is_active: tenant.is_active,
    });
    setModalError(null);
  };

  // ---- User handlers ----

  const openCreateUser = () => {
    setNewUser({ email: '', name: '', role: 'user', can_view_claims: true });
    setCreateSources(tenantSources.map(s => ({ ...s, assigned: true })));
    setModalError(null);
    setShowCreateUser(true);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!selectedTenant) return;
    setSaving(true);
    setModalError(null);
    try {
      const selectedSourceIds = createSources.filter(s => s.assigned).map(s => s.id);
      const payload = {
        email: newUser.email,
        name: newUser.name,
        role: newUser.role,
        can_view_claims: newUser.can_view_claims,
        source_ids: selectedSourceIds,
      };
      const res = await authFetch(`${API_BASE}/api/admin/tenants/${selectedTenant.id}/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to create user');
      }
      setShowCreateUser(false);
      setNewUser({ email: '', name: '', role: 'user', can_view_claims: true });
      setCreateSources([]);
      await fetchTenantUsers(selectedTenant.id);
    } catch (err) {
      setModalError(err.message);
    }
    setSaving(false);
  };

  const toggleCreateSource = (sourceId) => {
    setCreateSources(prev => prev.map(s => s.id === sourceId ? { ...s, assigned: !s.assigned } : s));
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!selectedTenant || !showEditUser) return;
    setSaving(true);
    setModalError(null);
    try {
      const res = await authFetch(
        `${API_BASE}/api/admin/tenants/${selectedTenant.id}/users/${showEditUser.id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(editUserData),
        }
      );
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to update user');
      }
      setShowEditUser(null);
      setEditUserData({});
      await fetchTenantUsers(selectedTenant.id);
    } catch (err) {
      setModalError(err.message);
    }
    setSaving(false);
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!selectedTenant) return;
    if (!window.confirm(`Are you sure you want to delete "${userName}"? This action cannot be undone.`)) return;
    try {
      const res = await authFetch(
        `${API_BASE}/api/admin/tenants/${selectedTenant.id}/users/${userId}`,
        { method: 'DELETE' }
      );
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to delete user');
      }
      await fetchTenantUsers(selectedTenant.id);
    } catch (err) {
      setError(err.message);
    }
  };

  const openEditUser = (user) => {
    setShowEditUser(user);
    setEditUserData({
      name: user.name,
      is_active: user.is_active,
      can_view_claims: user.can_view_claims,
      role: user.role,
    });
    setModalError(null);
  };

  // ---- Source handlers ----

  const openCreateSource = () => {
    setNewSource({ name: '', endpoint: '', company_id: 1, username: '', password: '' });
    setTestResult(null);
    setModalError(null);
    setShowCreateSource(true);
  };

  const handleCreateSource = async (e) => {
    e.preventDefault();
    if (!selectedTenant) return;
    setSaving(true);
    setModalError(null);
    try {
      const res = await authFetch(`${API_BASE}/api/admin/tenants/${selectedTenant.id}/sources`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newSource),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to create source');
      }
      setShowCreateSource(false);
      setNewSource({ name: '', endpoint: '', company_id: 1, username: '', password: '' });
      setTestResult(null);
      await fetchTenantSources(selectedTenant.id);
      await fetchTenants(); // refresh source count
    } catch (err) {
      setModalError(err.message);
    }
    setSaving(false);
  };

  const openEditSource = (source) => {
    setShowEditSource(source);
    setEditSourceData({
      name: source.name,
      endpoint: source.endpoint,
      company_id: source.company_id,
      is_enabled: source.is_enabled,
      username: '',
      password: '',
    });
    setTestResult(null);
    setModalError(null);
  };

  const handleUpdateSource = async (e) => {
    e.preventDefault();
    if (!selectedTenant || !showEditSource) return;
    setSaving(true);
    setModalError(null);
    try {
      // Only send fields that have values (don't send empty username/password)
      const payload = {};
      if (editSourceData.name !== showEditSource.name) payload.name = editSourceData.name;
      if (editSourceData.endpoint !== showEditSource.endpoint) payload.endpoint = editSourceData.endpoint;
      if (editSourceData.company_id !== showEditSource.company_id) payload.company_id = editSourceData.company_id;
      if (editSourceData.is_enabled !== showEditSource.is_enabled) payload.is_enabled = editSourceData.is_enabled;
      if (editSourceData.username) payload.username = editSourceData.username;
      if (editSourceData.password) payload.password = editSourceData.password;

      if (Object.keys(payload).length === 0) {
        setShowEditSource(null);
        setSaving(false);
        return;
      }

      const res = await authFetch(
        `${API_BASE}/api/admin/tenants/${selectedTenant.id}/sources/${showEditSource.id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to update source');
      }
      setShowEditSource(null);
      setEditSourceData({});
      setTestResult(null);
      await fetchTenantSources(selectedTenant.id);
    } catch (err) {
      setModalError(err.message);
    }
    setSaving(false);
  };

  const handleDeleteSource = async (sourceId, sourceName) => {
    if (!selectedTenant) return;
    if (!window.confirm(`Are you sure you want to delete source "${sourceName}"? This will remove access for all users.`)) return;
    try {
      const res = await authFetch(
        `${API_BASE}/api/admin/tenants/${selectedTenant.id}/sources/${sourceId}`,
        { method: 'DELETE' }
      );
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to delete source');
      }
      await fetchTenantSources(selectedTenant.id);
      await fetchTenants(); // refresh source count
    } catch (err) {
      setError(err.message);
    }
  };

  // ---- Test connection ----

  const handleTestNewSource = async () => {
    if (!newSource.endpoint || !newSource.username || !newSource.password) {
      setTestResult({ status: 'error', error: 'Endpoint, username, and password are required' });
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await authFetch(`${API_BASE}/api/admin/test-source`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint: newSource.endpoint,
          company_id: newSource.company_id,
          username: newSource.username,
          password: newSource.password,
        }),
      });
      const data = await res.json();
      setTestResult(data);
    } catch (err) {
      setTestResult({ status: 'offline', error: err.message });
    }
    setTesting(false);
  };

  const handleTestExistingSource = async (sourceId) => {
    if (!selectedTenant) return;
    setTesting(true);
    setTestResult(null);
    try {
      const res = await authFetch(
        `${API_BASE}/api/admin/tenants/${selectedTenant.id}/sources/${sourceId}/test`,
        { method: 'POST' }
      );
      const data = await res.json();
      setTestResult({ ...data, sourceId });
    } catch (err) {
      setTestResult({ status: 'offline', error: err.message, sourceId });
    }
    setTesting(false);
  };

  const handleTestEditSource = async () => {
    // If new credentials provided, test with those; otherwise test existing source
    if (editSourceData.username && editSourceData.password) {
      setTesting(true);
      setTestResult(null);
      try {
        const res = await authFetch(`${API_BASE}/api/admin/test-source`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            endpoint: editSourceData.endpoint,
            company_id: editSourceData.company_id,
            username: editSourceData.username,
            password: editSourceData.password,
          }),
        });
        const data = await res.json();
        setTestResult(data);
      } catch (err) {
        setTestResult({ status: 'offline', error: err.message });
      }
      setTesting(false);
    } else if (showEditSource) {
      await handleTestExistingSource(showEditSource.id);
    }
  };

  // ---- Helpers ----

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const ModalErrorBanner = () => {
    if (!modalError) return null;
    return (
      <div className="admin-error" style={{ margin: '0 0 12px 0' }}>
        {modalError}
        <button onClick={() => setModalError(null)} className="error-close">&times;</button>
      </div>
    );
  };

  const TestResultBadge = ({ result }) => {
    if (!result) return null;
    const color = result.status === 'online' ? '#16a34a' : result.status === 'degraded' ? '#d97706' : '#dc2626';
    return (
      <div className="admin-test-result" style={{ borderLeft: `3px solid ${color}`, padding: '8px 12px', marginTop: '8px', background: '#f9fafb', borderRadius: '4px' }}>
        <span style={{ color, fontWeight: 600, textTransform: 'uppercase', fontSize: '12px' }}>{result.status}</span>
        {result.response_time_ms && <span style={{ marginLeft: '8px', fontSize: '12px', color: '#6b7280' }}>{result.response_time_ms}ms</span>}
        {result.error && <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px' }}>{result.error}</div>}
      </div>
    );
  };

  if (loading && tenants.length === 0) {
    return (
      <div className="admin-page">
        <div className="admin-loading">
          <div className="spinner"></div>
          <p>Loading platform data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-layout">
        {/* Tenant sidebar */}
        <div className="admin-sidebar">
          <div className="admin-sidebar-header">
            <h3>Tenants</h3>
            <button className="admin-btn-primary admin-btn-sm" onClick={() => setShowCreateTenant(true)}>
              + New
            </button>
          </div>
          <div className="admin-sidebar-list">
            {tenants.map(t => (
              <div
                key={t.id}
                className={`admin-sidebar-item ${selectedTenant?.id === t.id ? 'active' : ''} ${!t.is_active ? 'inactive' : ''}`}
                onClick={() => setSelectedTenant(t)}
              >
                <div className="admin-sidebar-item-top">
                  <span className="admin-sidebar-item-name">{t.name}</span>
                  {!t.is_active && <span className="admin-badge-inactive">Inactive</span>}
                </div>
                <div className="admin-sidebar-item-meta">
                  <span>{t.user_count} user{t.user_count !== 1 ? 's' : ''}</span>
                  <span>{t.source_count} source{t.source_count !== 1 ? 's' : ''}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Main content */}
        <div className="admin-content">
          {error && (
            <div className="admin-error">
              {error}
              <button onClick={() => setError(null)} className="error-close">&times;</button>
            </div>
          )}

          {selectedTenant && (
            <>
              {/* Tenant header */}
              <div className="admin-content-header">
                <div className="admin-content-header-left">
                  <h2>{selectedTenant.name}</h2>
                  <span className="admin-slug">/{selectedTenant.slug}</span>
                  {!selectedTenant.is_active && <span className="admin-badge-inactive">Inactive</span>}
                </div>
                <button className="admin-btn-secondary admin-btn-sm" onClick={() => openEditTenant(selectedTenant)}>
                  Edit Tenant
                </button>
              </div>

              {/* Tabs */}
              <div className="admin-tabs">
                <button
                  className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
                  onClick={() => setActiveTab('users')}
                >
                  Users ({tenantUsers.length})
                </button>
                <button
                  className={`admin-tab ${activeTab === 'sources' ? 'active' : ''}`}
                  onClick={() => setActiveTab('sources')}
                >
                  Sources ({tenantSources.length})
                </button>
              </div>

              {/* Users Tab */}
              {activeTab === 'users' && (
                <div className="admin-table-container">
                  <div className="admin-table-header">
                    <h3>Users</h3>
                    <button className="admin-btn-primary admin-btn-sm" onClick={openCreateUser}>
                      + Create User
                    </button>
                  </div>
                  {tenantUsers.length === 0 ? (
                    <div className="admin-empty">No users in this tenant</div>
                  ) : (
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Claims</th>
                          <th>Sources</th>
                          <th>Status</th>
                          <th>Last Login</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tenantUsers.map(u => (
                          <tr key={u.id} className={!u.is_active ? 'row-inactive' : ''}>
                            <td className="td-name">{u.name}</td>
                            <td className="td-email">{u.email}</td>
                            <td>
                              <span className={`admin-role-badge role-${u.role}`}>{u.role}</span>
                            </td>
                            <td>
                              <span className={`admin-perm-badge ${u.can_view_claims ? 'perm-yes' : 'perm-no'}`}>
                                {u.can_view_claims ? 'Yes' : 'No'}
                              </span>
                            </td>
                            <td>{u.source_count}</td>
                            <td>
                              <span className={`admin-status-badge ${u.is_active ? 'status-active' : 'status-inactive'}`}>
                                {u.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="td-date">{formatDate(u.last_login)}</td>
                            <td className="td-actions">
                              <button className="admin-btn-link" onClick={() => openEditUser(u)}>Edit</button>
                              {u.role !== 'super_admin' && (
                                <button className="admin-btn-link admin-btn-danger" onClick={() => handleDeleteUser(u.id, u.name)}>Delete</button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* Sources Tab */}
              {activeTab === 'sources' && (
                <div className="admin-table-container">
                  <div className="admin-table-header">
                    <h3>Sources</h3>
                    <button className="admin-btn-primary admin-btn-sm" onClick={openCreateSource}>
                      + Create Source
                    </button>
                  </div>
                  {tenantSources.length === 0 ? (
                    <div className="admin-empty">No sources configured for this tenant</div>
                  ) : (
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Endpoint</th>
                          <th>Company ID</th>
                          <th>Status</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tenantSources.map(s => (
                          <tr key={s.id} className={!s.is_enabled ? 'row-inactive' : ''}>
                            <td className="td-name">{s.name}</td>
                            <td className="td-email">{s.endpoint}</td>
                            <td>{s.company_id}</td>
                            <td>
                              <span className={`admin-status-badge ${s.is_enabled ? 'status-active' : 'status-inactive'}`}>
                                {s.is_enabled ? 'Enabled' : 'Disabled'}
                              </span>
                              {testResult && testResult.sourceId === s.id && (
                                <span className={`admin-status-badge ${testResult.status === 'online' ? 'status-active' : 'status-inactive'}`} style={{ marginLeft: '4px' }}>
                                  {testResult.status}{testResult.response_time_ms ? ` (${testResult.response_time_ms}ms)` : ''}
                                </span>
                              )}
                            </td>
                            <td className="td-date">{formatDate(s.created_at)}</td>
                            <td className="td-actions">
                              <button className="admin-btn-link" onClick={() => handleTestExistingSource(s.id)} disabled={testing}>
                                {testing && testResult?.sourceId === s.id ? 'Testing...' : 'Test'}
                              </button>
                              <button className="admin-btn-link" onClick={() => openEditSource(s)}>Edit</button>
                              <button className="admin-btn-link admin-btn-danger" onClick={() => handleDeleteSource(s.id, s.name)}>Delete</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* CREATE TENANT MODAL */}
      {showCreateTenant && (
        <div className="admin-modal-overlay" onMouseDown={handleOverlayMouseDown} onClick={(e) => handleOverlayClick(e, () => setShowCreateTenant(false))}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Create Tenant</h3>
              <button className="admin-modal-close" onClick={() => setShowCreateTenant(false)}>&times;</button>
            </div>
            <form onSubmit={handleCreateTenant}>
              <div className="admin-modal-body">
                <ModalErrorBanner />
                <div className="admin-field">
                  <label>Name</label>
                  <input
                    type="text"
                    value={newTenant.name}
                    onChange={e => setNewTenant({ ...newTenant, name: e.target.value })}
                    placeholder="e.g. Hollard Insurance"
                    required
                    autoFocus
                  />
                </div>
                <div className="admin-field">
                  <label>Slug</label>
                  <input
                    type="text"
                    value={newTenant.slug}
                    onChange={e => setNewTenant({ ...newTenant, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    placeholder="e.g. hollard"
                    required
                  />
                  <span className="admin-field-hint">Lowercase letters, numbers, and hyphens only</span>
                </div>
              </div>
              <div className="admin-modal-footer">
                <button type="button" className="admin-btn-secondary" onClick={() => setShowCreateTenant(false)}>Cancel</button>
                <button type="submit" className="admin-btn-primary" disabled={saving}>
                  {saving ? 'Creating...' : 'Create Tenant'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CREATE USER MODAL */}
      {showCreateUser && (
        <div className="admin-modal-overlay" onMouseDown={handleOverlayMouseDown} onClick={(e) => handleOverlayClick(e, () => setShowCreateUser(false))}>
          <div className="admin-modal admin-modal-wide" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Create User</h3>
              <span className="admin-modal-subtitle">For {selectedTenant?.name}</span>
              <button className="admin-modal-close" onClick={() => setShowCreateUser(false)}>&times;</button>
            </div>
            <form onSubmit={handleCreateUser}>
              <div className="admin-modal-body">
                <ModalErrorBanner />
                <div className="admin-modal-columns">
                  {/* Left column - user details */}
                  <div className="admin-modal-col">
                    <div className="admin-field">
                      <label>Full Name</label>
                      <input
                        type="text"
                        value={newUser.name}
                        onChange={e => setNewUser({ ...newUser, name: e.target.value })}
                        placeholder="e.g. John Smith"
                        required
                        autoFocus
                      />
                    </div>
                    <div className="admin-field">
                      <label>Email</label>
                      <input
                        type="email"
                        value={newUser.email}
                        onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                        placeholder="john@hollard.co.za"
                        required
                      />
                    </div>
                    <div className="admin-field">
                      <label>Role</label>
                      <select
                        value={newUser.role}
                        onChange={e => setNewUser({ ...newUser, role: e.target.value })}
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                        <option value="super_admin">Super Admin</option>
                      </select>
                    </div>
                    <div className="admin-field-row">
                      <label className="admin-checkbox">
                        <input
                          type="checkbox"
                          checked={newUser.can_view_claims}
                          onChange={e => setNewUser({ ...newUser, can_view_claims: e.target.checked })}
                        />
                        <span>Can View Claims</span>
                      </label>
                    </div>
                  </div>

                  {/* Right column - source access */}
                  <div className="admin-modal-col">
                    <div className="admin-sources-header">
                      <label>Source Access</label>
                      <div className="admin-sources-actions">
                        <button type="button" className="admin-btn-link" onClick={() => setCreateSources(prev => prev.map(s => ({ ...s, assigned: true })))}>Select All</button>
                        <button type="button" className="admin-btn-link" onClick={() => setCreateSources(prev => prev.map(s => ({ ...s, assigned: false })))}>Deselect All</button>
                      </div>
                    </div>
                    <div className="admin-source-list">
                      {createSources.length === 0 ? (
                        <div className="admin-empty-sm">No sources available</div>
                      ) : (
                        createSources.map(s => (
                          <label key={s.id} className="admin-source-item">
                            <input
                              type="checkbox"
                              checked={s.assigned}
                              onChange={() => toggleCreateSource(s.id)}
                            />
                            <span className="admin-source-name">{s.name}</span>
                            {!s.is_enabled && <span className="admin-badge-inactive">Disabled</span>}
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="admin-modal-footer">
                <button type="button" className="admin-btn-secondary" onClick={() => setShowCreateUser(false)}>Cancel</button>
                <button type="submit" className="admin-btn-primary" disabled={saving}>
                  {saving ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT USER MODAL */}
      {showEditUser && (
        <div className="admin-modal-overlay" onMouseDown={handleOverlayMouseDown} onClick={(e) => handleOverlayClick(e, () => setShowEditUser(null))}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Edit User</h3>
              <span className="admin-modal-subtitle">{showEditUser.email}</span>
              <button className="admin-modal-close" onClick={() => setShowEditUser(null)}>&times;</button>
            </div>
            <form onSubmit={handleUpdateUser}>
              <div className="admin-modal-body">
                <ModalErrorBanner />
                <div className="admin-field">
                  <label>Name</label>
                  <input
                    type="text"
                    value={editUserData.name || ''}
                    onChange={e => setEditUserData({ ...editUserData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="admin-field">
                  <label>Role</label>
                  <select
                    value={editUserData.role || 'user'}
                    onChange={e => setEditUserData({ ...editUserData, role: e.target.value })}
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
                <div className="admin-field-row">
                  <label className="admin-checkbox">
                    <input
                      type="checkbox"
                      checked={editUserData.is_active !== false}
                      onChange={e => setEditUserData({ ...editUserData, is_active: e.target.checked })}
                    />
                    <span>Active</span>
                  </label>
                  <label className="admin-checkbox">
                    <input
                      type="checkbox"
                      checked={editUserData.can_view_claims !== false}
                      onChange={e => setEditUserData({ ...editUserData, can_view_claims: e.target.checked })}
                    />
                    <span>Can View Claims</span>
                  </label>
                </div>
              </div>
              <div className="admin-modal-footer">
                <button type="button" className="admin-btn-secondary" onClick={() => setShowEditUser(null)}>Cancel</button>
                <button type="submit" className="admin-btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT TENANT MODAL */}
      {showEditTenant && (
        <div className="admin-modal-overlay" onMouseDown={handleOverlayMouseDown} onClick={(e) => handleOverlayClick(e, () => setShowEditTenant(null))}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Edit Tenant</h3>
              <span className="admin-modal-subtitle">/{showEditTenant.slug}</span>
              <button className="admin-modal-close" onClick={() => setShowEditTenant(null)}>&times;</button>
            </div>
            <form onSubmit={handleUpdateTenant}>
              <div className="admin-modal-body">
                <ModalErrorBanner />
                <div className="admin-field">
                  <label>Name</label>
                  <input
                    type="text"
                    value={editTenantData.name || ''}
                    onChange={e => setEditTenantData({ ...editTenantData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="admin-field-row">
                  <label className="admin-checkbox">
                    <input
                      type="checkbox"
                      checked={editTenantData.is_active !== false}
                      onChange={e => setEditTenantData({ ...editTenantData, is_active: e.target.checked })}
                    />
                    <span>Active</span>
                  </label>
                </div>
              </div>
              <div className="admin-modal-footer">
                <button type="button" className="admin-btn-secondary" onClick={() => setShowEditTenant(null)}>Cancel</button>
                <button type="submit" className="admin-btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CREATE SOURCE MODAL */}
      {showCreateSource && (
        <div className="admin-modal-overlay" onMouseDown={handleOverlayMouseDown} onClick={(e) => handleOverlayClick(e, () => setShowCreateSource(false))}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Create Source</h3>
              <span className="admin-modal-subtitle">For {selectedTenant?.name}</span>
              <button className="admin-modal-close" onClick={() => setShowCreateSource(false)}>&times;</button>
            </div>
            <form onSubmit={handleCreateSource}>
              <div className="admin-modal-body">
                <ModalErrorBanner />
                <div className="admin-field">
                  <label>Name</label>
                  <input
                    type="text"
                    value={newSource.name}
                    onChange={e => setNewSource({ ...newSource, name: e.target.value })}
                    placeholder="e.g. BSure Firtech"
                    required
                    autoFocus
                  />
                </div>
                <div className="admin-field">
                  <label>Endpoint URL</label>
                  <input
                    type="url"
                    value={newSource.endpoint}
                    onChange={e => setNewSource({ ...newSource, endpoint: e.target.value })}
                    placeholder="https://example.com/TialWebService/service.asmx"
                    required
                  />
                </div>
                <div className="admin-field">
                  <label>Company ID</label>
                  <input
                    type="number"
                    value={newSource.company_id}
                    onChange={e => setNewSource({ ...newSource, company_id: parseInt(e.target.value) || 1 })}
                    min={1}
                    required
                  />
                </div>
                <div className="admin-field">
                  <label>Username</label>
                  <input
                    type="text"
                    value={newSource.username}
                    onChange={e => setNewSource({ ...newSource, username: e.target.value })}
                    placeholder="API username"
                    required
                  />
                </div>
                <div className="admin-field">
                  <label>Password</label>
                  <input
                    type="password"
                    value={newSource.password}
                    onChange={e => setNewSource({ ...newSource, password: e.target.value })}
                    placeholder="API password"
                    required
                  />
                </div>
                <div style={{ marginTop: '8px' }}>
                  <button type="button" className="admin-btn-secondary admin-btn-sm" onClick={handleTestNewSource} disabled={testing}>
                    {testing ? 'Testing...' : 'Test Connection'}
                  </button>
                  <TestResultBadge result={testResult} />
                </div>
              </div>
              <div className="admin-modal-footer">
                <button type="button" className="admin-btn-secondary" onClick={() => setShowCreateSource(false)}>Cancel</button>
                <button type="submit" className="admin-btn-primary" disabled={saving}>
                  {saving ? 'Creating...' : 'Create Source'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT SOURCE MODAL */}
      {showEditSource && (
        <div className="admin-modal-overlay" onMouseDown={handleOverlayMouseDown} onClick={(e) => handleOverlayClick(e, () => { setShowEditSource(null); setTestResult(null); })}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Edit Source</h3>
              <span className="admin-modal-subtitle">{showEditSource.name}</span>
              <button className="admin-modal-close" onClick={() => { setShowEditSource(null); setTestResult(null); }}>&times;</button>
            </div>
            <form onSubmit={handleUpdateSource}>
              <div className="admin-modal-body">
                <ModalErrorBanner />
                <div className="admin-field">
                  <label>Name</label>
                  <input
                    type="text"
                    value={editSourceData.name || ''}
                    onChange={e => setEditSourceData({ ...editSourceData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="admin-field">
                  <label>Endpoint URL</label>
                  <input
                    type="url"
                    value={editSourceData.endpoint || ''}
                    onChange={e => setEditSourceData({ ...editSourceData, endpoint: e.target.value })}
                    required
                  />
                </div>
                <div className="admin-field">
                  <label>Company ID</label>
                  <input
                    type="number"
                    value={editSourceData.company_id || 1}
                    onChange={e => setEditSourceData({ ...editSourceData, company_id: parseInt(e.target.value) || 1 })}
                    min={1}
                    required
                  />
                </div>
                <div className="admin-field">
                  <label>Username</label>
                  <input
                    type="text"
                    value={editSourceData.username || ''}
                    onChange={e => setEditSourceData({ ...editSourceData, username: e.target.value })}
                    placeholder="Leave blank to keep current"
                  />
                </div>
                <div className="admin-field">
                  <label>Password</label>
                  <input
                    type="password"
                    value={editSourceData.password || ''}
                    onChange={e => setEditSourceData({ ...editSourceData, password: e.target.value })}
                    placeholder="Leave blank to keep current"
                  />
                </div>
                <div className="admin-field-row">
                  <label className="admin-checkbox">
                    <input
                      type="checkbox"
                      checked={editSourceData.is_enabled !== false}
                      onChange={e => setEditSourceData({ ...editSourceData, is_enabled: e.target.checked })}
                    />
                    <span>Enabled</span>
                  </label>
                </div>
                <div style={{ marginTop: '8px' }}>
                  <button type="button" className="admin-btn-secondary admin-btn-sm" onClick={handleTestEditSource} disabled={testing}>
                    {testing ? 'Testing...' : 'Test Connection'}
                  </button>
                  {!editSourceData.username && !editSourceData.password && (
                    <span style={{ fontSize: '11px', color: '#6b7280', marginLeft: '8px' }}>Tests with saved credentials</span>
                  )}
                  <TestResultBadge result={testResult} />
                </div>
              </div>
              <div className="admin-modal-footer">
                <button type="button" className="admin-btn-secondary" onClick={() => { setShowEditSource(null); setTestResult(null); }}>Cancel</button>
                <button type="submit" className="admin-btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuperAdmin;
